// import { PageEvent } from '@angular/material/paginator';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  ViewChild,
} from '@angular/core';

// INTERFACES
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params } from '@angular/router';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, takeUntil, catchError } from 'rxjs/operators';

import { PaginationConfig } from '../../../shared/configs/paginator.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import { OptionInterface } from '../../../shared/interfaces/common.interface';
import { LocalStorageKeys } from '../../../shared/interfaces/localstorage.interface';
import {
  IPaginatorEvent,
  IPagination,
} from '../../../shared/interfaces/paginator.interface';

// COMPONENTS

// SERVICES
import { ProductSubstitutionApiService } from '../../../shared/services/api/product-substitution-api.service';
import { IRfqProductSubstitution } from '../../interfaces/rfq.interface';
import { RfqListDropdownComponent } from '../rfq-list-dropdown/rfq-list-dropdown.component';

@Component({
  selector: 'lum-rfq-product-substitution',
  templateUrl: './rfq-product-substitution.component.html',
  styleUrls: ['./rfq-product-substitution.component.scss'],
})
export class RfqProductSubstitutionComponent implements OnInit, OnDestroy {
  @ViewChild('searchSelectComponent', { static: false })
  searchSelectComponent :RfqListDropdownComponent;

  @Input()
  productType :string = '';

  @Input()
  productId :number = 0;

  searchTerm :string = '';

  substituteProductId :number = 0;

  currentProductSubstitutions :IRfqProductSubstitution[];

  loading :boolean = false;
  submittingForm :boolean = false;

  private onDestroy$ = new Subject();

  constructor(
    private productSubstitutionApiService :ProductSubstitutionApiService,
    private route :ActivatedRoute,
    private cd :ChangeDetectorRef,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.getRouterParams().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  private loadProductSubstitutes(productId :number) :void {
    this.loading = true;
    this.productSubstitutionApiService
      .getProductSubstitutesByProductId({
        params: {
          productId: this.productId.toString(),
          relations: 'substituteProduct',
        },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((currentProductSubstitutions) => {
        this.loading = false;
        this.currentProductSubstitutions = currentProductSubstitutions;
        this.currentProductSubstitutions.forEach(
          (item :IRfqProductSubstitution, index :number) => {
            item.priority = index + 1;
          },
        );
        this.updatePriorities();
      });
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const { id } = params;
        if (id) {
          this.productId = id;
          this.loadProductSubstitutes(this.productId);
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  onDetachEvent(index :number) :void {
    this.loading = true;
    const productSubstitutionId = this.currentProductSubstitutions[index]?.id;
    if (productSubstitutionId) {
      this.productSubstitutionApiService
        .detachProductSubstitute(productSubstitutionId)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response :{ success :string }) => {
            this.loading = false;
            this.snackBar.open(response.success, 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            const productSubstitutionIndex =
              this.currentProductSubstitutions.findIndex(
                (row :any) => row.id == productSubstitutionId,
              );
            this.currentProductSubstitutions.splice(productSubstitutionIndex, 1);
            this.currentProductSubstitutions.forEach(
              (item :IRfqProductSubstitution, index :number) => {
                item.priority = index + 1;
              },
            );
            this.updatePriorities();
            this.cd.detectChanges();
          },
          error: (response) => {
            this.loading = false;
            this.snackBar.open(response.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    }
  }

  onSelectEvent(data :any) :void {
    this.substituteProductId = data.rfq.id;
  }

  onAdd() :void {
    if (this.substituteProductId) {
      this.submittingForm = true;
      let priority = 1;

      if (this.currentProductSubstitutions.length > 1) {
        const newestProductSubstitutionPriority =
          this.currentProductSubstitutions.reduce((prev, current) => {
            return prev.priority > current.priority ? prev : current;
          });

        priority = newestProductSubstitutionPriority.priority + priority;
      }

      this.productSubstitutionApiService
        .addProductSubstitute(this.productId, this.substituteProductId, priority)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response) => {
            this.submittingForm = false;
            this.snackBar.open(
              'Product substitution successfully added.',
              'Success',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
            this.currentProductSubstitutions.push(response);
            this.currentProductSubstitutions.forEach(
              (item :IRfqProductSubstitution, index :number) => {
                item.priority = index + 1;
              },
            );
            this.updatePriorities();
            this.resetForm();
            this.cd.detectChanges();
          },
          error: (response) => {
            this.submittingForm = false;
            this.snackBar.open(response.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.resetForm();
          },
        });
    }
  }

  onDropEvent(event :CdkDragDrop<IRfqProductSubstitution[]>) :void {
    const previousIndex = event.item?.data?.priority - 1;
    const currentIndex = this.filteredTableData[event.currentIndex]?.priority - 1;

    moveItemInArray(this.currentProductSubstitutions, previousIndex, currentIndex);

    const priorities = this.filteredTableData
      .map((obj) => obj.priority)
      .sort((a, b) => a - b);
    this.filteredTableData.forEach(
      (item :IRfqProductSubstitution, index :number) => {
        item.priority = priorities[index];
      },
    );
    this.updatePriorities();
  }

  updatePriorities() :void {
    const updatedItemPriorities = this.currentProductSubstitutions.map((item) => {
      return { id: item.id, priority: item.priority };
    });

    this.productSubstitutionApiService
      .updatePriorities(updatedItemPriorities)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        error: (response) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  resetForm() :void {
    this.substituteProductId = 0;
    this.searchSelectComponent.reset();
  }

  get filteredTableData() :any[] {
    if (!this.searchTerm) {
      return this.currentProductSubstitutions;
    }

    return this.currentProductSubstitutions.filter(
      (item) =>
        item?.substituteProduct?.internalSku
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase()) ||
        item?.substituteProduct?.productName
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase()),
    );
  }
}
